/**
 * Service pour exporter les commandes fournisseurs en différents formats
 * Version simplifiée utilisant les APIs natives du navigateur
 */

import { CommandeGroupee } from './CommandeFournisseurService';

class ExportCommandeService {
  /**
   * Formate une date en français
   */
  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Formate un montant en euros
   */
  private formatMontant(montant: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  }

  /**
   * Génère un HTML formaté pour impression ou export PDF via le navigateur
   */
  private generateHTML(commande: CommandeGroupee): string {
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Commande ${commande.numeroCommande}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .section { margin-bottom: 30px; }
          .section h2 { color: #333; border-bottom: 2px solid #007bff; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f8f9fa; font-weight: bold; }
          .text-right { text-align: right; }
          .total-row { background-color: #e9ecef; font-weight: bold; }
          .stats { background-color: #e3f2fd; padding: 15px; border-radius: 5px; }
          .devis-list { background-color: #f8f9fa; padding: 15px; border-radius: 5px; }
          .devis-tag { display: inline-block; background-color: #007bff; color: white; padding: 2px 8px; margin: 2px; border-radius: 3px; font-size: 12px; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>COMMANDE FOURNISSEUR</h1>
          <h2>${commande.numeroCommande}</h2>
        </div>
        
        <div class="info-grid">
          <div>
            <strong>Date:</strong> ${this.formatDate(commande.dateCreation)}<br>
            <strong>Nombre de devis:</strong> ${commande.totaux.nombreDevis}
          </div>
          <div>
            <strong>Total général:</strong> ${this.formatMontant(commande.totaux.montantTotal)}
          </div>
        </div>`;

    // Statistiques
    if (commande.statistiques) {
      html += `
        <div class="section">
          <h2>📊 Statistiques</h2>
          <div class="stats">
            <strong>Matières exclues (en stock):</strong> ${commande.statistiques.matieresExcluesStock}<br>
            <strong>Matières à commander:</strong> ${commande.statistiques.matieresACommander}
          </div>
        </div>`;
    }

    // Matières
    if (commande.matieres.length > 0) {
      html += `
        <div class="section">
          <h2>🧵 Matières à commander (${this.formatMontant(commande.totaux.montantMatieres)})</h2>
          <table>
            <thead>
              <tr>
                <th>Matière</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total</th>
                <th>Devis d'origine</th>
              </tr>
            </thead>
            <tbody>`;
      
      commande.matieres.forEach(matiere => {
        html += `
              <tr>
                <td>${matiere.nom}</td>
                <td class="text-right">${matiere.quantiteMetres.toFixed(2)} ml</td>
                <td class="text-right">${this.formatMontant(matiere.prixUnitaire)}/ml</td>
                <td class="text-right">${this.formatMontant(matiere.montantTotal)}</td>
                <td>${matiere.devisOrigines.map(d => d.reference).join(', ')}</td>
              </tr>`;
      });
      
      html += `
              <tr class="total-row">
                <td colspan="3"><strong>TOTAL MATIÈRES</strong></td>
                <td class="text-right"><strong>${this.formatMontant(commande.totaux.montantMatieres)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>`;
    }

    // Fournitures
    if (commande.fournitures.length > 0) {
      html += `
        <div class="section">
          <h2>🔧 Fournitures (${this.formatMontant(commande.totaux.montantFournitures)})</h2>
          <table>
            <thead>
              <tr>
                <th>Fourniture</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total</th>
                <th>Devis d'origine</th>
              </tr>
            </thead>
            <tbody>`;
      
      commande.fournitures.forEach(fourniture => {
        html += `
              <tr>
                <td>${fourniture.nom}</td>
                <td class="text-right">${fourniture.quantite.toFixed(2)} ${fourniture.unite}</td>
                <td class="text-right">${this.formatMontant(fourniture.prixUnitaire)}/${fourniture.unite}</td>
                <td class="text-right">${this.formatMontant(fourniture.montantTotal)}</td>
                <td>${fourniture.devisOrigines.map(d => d.reference).join(', ')}</td>
              </tr>`;
      });
      
      html += `
              <tr class="total-row">
                <td colspan="3"><strong>TOTAL FOURNITURES</strong></td>
                <td class="text-right"><strong>${this.formatMontant(commande.totaux.montantFournitures)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>`;
    }

    // Devis inclus
    html += `
        <div class="section">
          <h2>📋 Devis inclus dans cette commande</h2>
          <div class="devis-list">`;
    
    commande.devisInclus.forEach(reference => {
      html += `<span class="devis-tag">${reference}</span>`;
    });
    
    html += `
          </div>
        </div>
        
        <div class="section">
          <h2>💰 Résumé financier</h2>
          <table style="max-width: 400px;">
            <tr>
              <td><strong>Matières</strong></td>
              <td class="text-right">${this.formatMontant(commande.totaux.montantMatieres)}</td>
            </tr>
            <tr>
              <td><strong>Fournitures</strong></td>
              <td class="text-right">${this.formatMontant(commande.totaux.montantFournitures)}</td>
            </tr>
            <tr class="total-row">
              <td><strong>TOTAL GÉNÉRAL</strong></td>
              <td class="text-right"><strong>${this.formatMontant(commande.totaux.montantTotal)}</strong></td>
            </tr>
          </table>
        </div>
      </body>
      </html>`;

    return html;
  }

  /**
   * Exporte la commande en ouvrant une fenêtre d'impression (PDF via navigateur)
   */
  async exportToPDF(commande: CommandeGroupee): Promise<void> {
    try {
      const html = this.generateHTML(commande);
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        
        // Attendre que le contenu soit chargé puis lancer l'impression
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        throw new Error('Impossible d\'ouvrir la fenêtre d\'impression');
      }
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      throw new Error('Erreur lors de la génération du PDF');
    }
  }

  /**
   * Exporte la commande en Excel (format CSV compatible Excel)
   */
  async exportToExcel(commande: CommandeGroupee): Promise<void> {
    try {
      let csvContent = '\uFEFF'; // BOM pour UTF-8
      
      // En-tête
      csvContent += 'COMMANDE FOURNISSEUR\n';
      csvContent += `Numéro;${commande.numeroCommande}\n`;
      csvContent += `Date;${this.formatDate(commande.dateCreation)}\n`;
      csvContent += `Nombre de devis;${commande.totaux.nombreDevis}\n`;
      csvContent += `Total;${commande.totaux.montantTotal.toFixed(2)}\n\n`;

      // Statistiques
      if (commande.statistiques) {
        csvContent += 'STATISTIQUES\n';
        csvContent += `Matières exclues (en stock);${commande.statistiques.matieresExcluesStock}\n`;
        csvContent += `Matières à commander;${commande.statistiques.matieresACommander}\n\n`;
      }

      // Matières
      if (commande.matieres.length > 0) {
        csvContent += 'MATIÈRES À COMMANDER\n';
        csvContent += 'Matière;Type;Couleur;Quantité (ml);Prix unitaire (€/ml);Montant total (€);Devis d\'origine\n';
        
        commande.matieres.forEach(matiere => {
          csvContent += `"${matiere.nom}";"${matiere.type}";"${matiere.couleur}";${matiere.quantiteMetres.toFixed(2)};${matiere.prixUnitaire.toFixed(2)};${matiere.montantTotal.toFixed(2)};"${matiere.devisOrigines.map(d => d.reference).join(', ')}"\n`;
        });
        
        csvContent += `TOTAL MATIÈRES;;;;${commande.totaux.montantMatieres.toFixed(2)};\n\n`;
      }

      // Fournitures
      if (commande.fournitures.length > 0) {
        csvContent += 'FOURNITURES\n';
        csvContent += 'Fourniture;Type;Quantité;Unité;Prix unitaire (€);Montant total (€);Devis d\'origine\n';
        
        commande.fournitures.forEach(fourniture => {
          csvContent += `"${fourniture.nom}";"${fourniture.type}";${fourniture.quantite.toFixed(2)};"${fourniture.unite}";${fourniture.prixUnitaire.toFixed(2)};${fourniture.montantTotal.toFixed(2)};"${fourniture.devisOrigines.map(d => d.reference).join(', ')}"\n`;
        });
        
        csvContent += `TOTAL FOURNITURES;;;;;${commande.totaux.montantFournitures.toFixed(2)};\n\n`;
      }

      // Devis inclus
      csvContent += 'DEVIS INCLUS\n';
      commande.devisInclus.forEach(ref => {
        csvContent += `${ref}\n`;
      });

      // Créer et télécharger le fichier
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${commande.numeroCommande}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      throw new Error('Erreur lors de la génération du fichier Excel');
    }
  }

  /**
   * Exporte la commande au format CSV simple
   */
  async exportToCSV(commande: CommandeGroupee): Promise<void> {
    try {
      let csvContent = '';
      
      // En-tête
      csvContent += `COMMANDE FOURNISSEUR;${commande.numeroCommande}\n`;
      csvContent += `Date;${this.formatDate(commande.dateCreation)}\n`;
      csvContent += `Nombre de devis;${commande.totaux.nombreDevis}\n`;
      csvContent += `Total;${this.formatMontant(commande.totaux.montantTotal)}\n\n`;

      // Matières
      if (commande.matieres.length > 0) {
        csvContent += 'MATIÈRES À COMMANDER\n';
        csvContent += 'Matière;Quantité (ml);Prix unitaire (€/ml);Montant total (€);Devis\n';
        
        commande.matieres.forEach(matiere => {
          csvContent += `${matiere.nom};${matiere.quantiteMetres.toFixed(2)};${matiere.prixUnitaire.toFixed(2)};${matiere.montantTotal.toFixed(2)};${matiere.devisOrigines.map(d => d.reference).join(', ')}\n`;
        });
        
        csvContent += `TOTAL MATIÈRES;;;${commande.totaux.montantMatieres.toFixed(2)};\n\n`;
      }

      // Fournitures
      if (commande.fournitures.length > 0) {
        csvContent += 'FOURNITURES\n';
        csvContent += 'Fourniture;Quantité;Unité;Prix unitaire (€);Montant total (€);Devis\n';
        
        commande.fournitures.forEach(fourniture => {
          csvContent += `${fourniture.nom};${fourniture.quantite.toFixed(2)};${fourniture.unite};${fourniture.prixUnitaire.toFixed(2)};${fourniture.montantTotal.toFixed(2)};${fourniture.devisOrigines.map(d => d.reference).join(', ')}\n`;
        });
        
        csvContent += `TOTAL FOURNITURES;;;;${commande.totaux.montantFournitures.toFixed(2)};\n\n`;
      }

      // Devis inclus
      csvContent += 'DEVIS INCLUS\n';
      csvContent += commande.devisInclus.join(';') + '\n';

      // Créer et télécharger le fichier CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${commande.numeroCommande}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
      throw new Error('Erreur lors de la génération du fichier CSV');
    }
  }
}

// Exporter une instance unique du service
export const exportCommandeService = new ExportCommandeService();
